import React from "react";
import QuadrantIndicator from "./QuadrantIndicator";

function Bar({ value, max, tone }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const color = tone === "high" ? "bg-red-500" : tone === "warn" ? "bg-yellow-400" : "bg-emerald-400";
  return (
    <div className="h-1 bg-white/10 w-full rounded-sm overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${pct}%`, transition: "width .4s ease" }} />
    </div>
  );
}

function PhaseRow({ label, v, i, vMax, iMax, testid }) {
  const vTone = v > 250 ? "high" : v < 210 ? "warn" : "ok";
  const iTone = i > 150 ? "high" : i > 120 ? "warn" : "ok";
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="label-tech">{label}</span>
        <div className="flex items-baseline gap-3">
          <span className="font-mono-tech text-lg">{v?.toFixed?.(1) ?? "—"}<span className="label-tech ml-1">V</span></span>
          <span className="font-mono-tech text-lg text-zinc-400">{i?.toFixed?.(1) ?? "—"}<span className="label-tech ml-1">A</span></span>
        </div>
      </div>
      <Bar value={v || 0} max={vMax} tone={vTone} />
      <Bar value={i || 0} max={iMax} tone={iTone} />
    </div>
  );
}

export default function PhasePanel({ latest }) {
  const v1 = +latest?.Voltage_v1n || 0;
  const v2 = +latest?.Voltage_v2n || 0;
  const v3 = +latest?.Voltage_v3n || 0;
  const i1 = +latest?.Current_I1 || 0;
  const i2 = +latest?.Current_I2 || 0;
  const i3 = +latest?.Current_I3 || 0;
  const nI = +latest?.Neutral_current || 0;

  const avgI = (i1 + i2 + i3) / 3;
  const unbalance = avgI ? Math.max(Math.abs(i1 - avgI), Math.abs(i2 - avgI), Math.abs(i3 - avgI)) / avgI * 100 : 0;
  const unbalTone = unbalance > 15 ? "text-red-400" : unbalance > 8 ? "text-yellow-400" : "text-emerald-400";

  return (
    <section className="panel p-5" data-testid="phase-panel">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading font-semibold text-base tracking-tight">3-PHASE STATUS</h3>
        <div className="label-tech">L–N / A</div>
      </div>

      <div className="space-y-5">
        <PhaseRow label="L1" v={v1} i={i1} vMax={260} iMax={160} testid="phase-l1" />
        <PhaseRow label="L2" v={v2} i={i2} vMax={260} iMax={160} testid="phase-l2" />
        <PhaseRow label="L3" v={v3} i={i3} vMax={260} iMax={160} testid="phase-l3" />
      </div>

      <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-2 gap-4">
        <div>
          <div className="label-tech">NEUTRAL I</div>
          <div className="font-mono-tech text-xl mt-1">{nI.toFixed(1)} <span className="label-tech">A</span></div>
        </div>
        <div>
          <div className="label-tech">UNBALANCE</div>
          <div className={`font-mono-tech text-xl mt-1 ${unbalTone}`}>{unbalance.toFixed(1)}%</div>
        </div>
        <div>
          <div className="label-tech">V12 / V23 / V31</div>
          <div className="font-mono-tech text-xs mt-1 text-zinc-300">
            {(+latest?.Voltage_v12 || 0).toFixed(0)} · {(+latest?.Voltage_v23 || 0).toFixed(0)} · {(+latest?.Voltage_v31 || 0).toFixed(0)} V
          </div>
        </div>
        <div>
          <div className="label-tech">AUX STATUS</div>
          <div className="font-mono-tech text-xl mt-1 text-emerald-400">
            {String(latest?.Aux_fail || "0") === "0" ? "OK" : "FAIL"}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <QuadrantIndicator latest={latest} />
      </div>
    </section>
  );
}
